import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import {Drawer} from 'rsuite';
import Swal, {type SweetAlertOptions} from 'sweetalert2';
import * as Yup from 'yup';

import {SWAL_SETTINGS} from '../../../../../utils/Constants';
import {handleServerValidations} from '../../../../../utils/commonfunction';
import CustomError from '../../../../../utils/customError';
import {editReceipt} from '../../../services/receipt.services';
import ReceiptQrPreview from './ReceiptQrPreview';
import {getHttpUrl, type ReceiptQrSettings} from './receiptQr';

interface ReceiptQrEditorProps {
  open: boolean;
  receipt: ReceiptQrSettings;
  onClose: () => void;
  onSaved: () => void;
}

interface ReceiptQrFormValues {
  qr_code_msg: string;
  qr_code_url: string;
}

interface SaveResponse {
  success?: boolean;
  message?: string;
}

const toastSettings = SWAL_SETTINGS as SweetAlertOptions;

const ReceiptQrEditor = ({open, receipt, onClose, onSaved}: ReceiptQrEditorProps) => {
  const {t} = useTranslation();
  const hasLegacyQr = Boolean(receipt.qr_code_image && !receipt.qr_code_url);
  const validationSchema = Yup.object().shape({
    qr_code_url: Yup.string()
      .test('required', 'qr_code_url_required', value => Boolean(value?.trim()) || hasLegacyQr)
      .test('httpUrl', 'qr_code_url_invalid', value => !value?.trim() || Boolean(getHttpUrl(value))),
  });
  const form = useFormik<ReceiptQrFormValues>({
    initialValues: {
      qr_code_msg: receipt.qr_code_msg && receipt.qr_code_msg !== 'null' ? receipt.qr_code_msg : '',
      qr_code_url: receipt.qr_code_url || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async values => {
      const data = new FormData();
      data.append('slug', receipt.slug ?? '');
      data.append('qr_code_msg', values.qr_code_msg);
      const qrUrl = values.qr_code_url.trim();
      if (qrUrl && qrUrl !== receipt.qr_code_url) data.append('qr_code_url', qrUrl);

      try {
        const response = (await editReceipt(data)) as SaveResponse;
        if (!response.success) {
          await Swal.fire({icon: 'error', text: handleServerValidations(response), ...toastSettings});
          return;
        }

        form.resetForm();
        onSaved();
        await Swal.fire({icon: 'success', text: response.message, ...toastSettings});
      } catch (error) {
        console.error('Error updating receipt', error);
        await Swal.fire({
          icon: 'error',
          text: `${t('label_bulk_settlement_error')}. ${t('try_again')}`,
          ...toastSettings,
        });
      }
    },
  });

  return (
    <Drawer open={open}>
      <Drawer.Header closeButton={false}>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <h5 className="mb-0">{t('label_QR_Code_Message')}</h5>
          <button type="button" className="btn-icon" aria-label={t('custom_item_close')} onClick={onClose}>
            <i className="ri-close-fill" aria-hidden="true" />
          </button>
        </div>
      </Drawer.Header>
      <Drawer.Body>
        <form onSubmit={form.handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="qr_code_msg">{t('QR_Code_Message')}</label>
            <input
              id="qr_code_msg"
              type="text"
              className="form-control"
              name="qr_code_msg"
              value={form.values.qr_code_msg}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="qr_code_url">
              {t('qr_code_url_label')} {!hasLegacyQr && <span>*</span>}
            </label>
            <input
              id="qr_code_url"
              className="form-control"
              name="qr_code_url"
              type="text"
              inputMode="url"
              aria-required={!hasLegacyQr}
              value={form.values.qr_code_url}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
            <CustomError className="text-danger" name="qr_code_url" form={form} />
            <ReceiptQrPreview
              url={form.values.qr_code_url}
              className="mt-3 mb-0 text-center"
            />
          </div>

          <div className="button-form">
            <button
              type="submit"
              disabled={form.isSubmitting || !receipt.slug}
              className={`btn btn-primary w-100${form.isSubmitting ? ' btn-loader' : ''}`}
            >
              {t('btn_submit')}
            </button>
          </div>
        </form>
      </Drawer.Body>
    </Drawer>
  );
};

export default ReceiptQrEditor;
